import { useI18n } from "@/lib/i18n";
import { ArrowUpRight } from "lucide-react";

// Rewrite Unsplash URLs for optimal delivery at the display size we actually use
// (card image is ~600px wide on desktop, full-width stacked on mobile). Returns
// the original URL unchanged for non-Unsplash sources so CMS-supplied images
// from other origins stay untouched.
function optimizeImageUrl(url, w = 900) {
    if (!url || typeof url !== "string") return url;
    if (!url.includes("images.unsplash.com")) return url;
    try {
        const u = new URL(url);
        u.searchParams.set("w", String(w));
        u.searchParams.set("q", "72");
        u.searchParams.set("fm", "webp");
        u.searchParams.set("auto", "format,compress");
        return u.toString();
    } catch {
        return url;
    }
}

export default function CaseStudies({ cases = [] }) {
    const { t, pick } = useI18n();
    if (!cases.length) return null;

    return (
        <section
            id="projekte"
            data-testid="cases-section"
            className="relative py-24 sm:py-28"
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <div className="max-w-2xl mb-12 lg:mb-16">
                    <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[#10B981]">
                        {t("sections.cases_eyebrow")}
                    </div>
                    <h2 className="mt-3 font-display font-bold tracking-[-0.01em] text-[#F1F5F9] text-[1.9rem] sm:text-[2.25rem] lg:text-[2.75rem] leading-[1.15]">
                        {t("sections.cases_title")}
                    </h2>
                    <p className="mt-4 text-[#94A3B8] text-base sm:text-lg leading-relaxed max-w-xl">
                        {t("sections.cases_sub")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
                    {cases.slice(0, 4).map((c, idx) => (
                        <CaseCard key={c.id || idx} item={c} pick={pick} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function CaseCard({ item, pick }) {
    return (
        <article
            data-testid={`case-card-${item.id}`}
            className="group relative overflow-hidden rounded-3xl border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.14] hover:bg-white/[0.04] transition-all duration-500"
        >
            {/* Cinematic image */}
            <div className="relative aspect-[16/10] overflow-hidden">
                {item.image ? (
                    <img
                        src={optimizeImageUrl(item.image, 900)}
                        srcSet={`${optimizeImageUrl(item.image, 600)} 600w, ${optimizeImageUrl(item.image, 900)} 900w, ${optimizeImageUrl(item.image, 1200)} 1200w`}
                        sizes="(max-width: 768px) 100vw, 600px"
                        alt={pick(item.title)}
                        loading="lazy"
                        decoding="async"
                        width="900"
                        height="563"
                        className="absolute inset-0 h-full w-full object-cover scale-100 group-hover:scale-[1.05] transition-transform duration-[1500ms] ease-out brightness-[0.78] contrast-[1.05] saturate-[0.85]"
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#111827] to-[#0F172A]" />
                )}
                {/* Dark gradient overlay for legibility + dark-theme cohesion */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1d] via-[#0a0f1d]/55 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#0a0f1d]/40" />

                {/* Tag */}
                <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C8A96B] bg-[#0F172A]/70 backdrop-blur-md border border-white/10">
                        {pick(item.tag)}
                    </span>
                </div>

                {/* Headline + outcome on image */}
                <div className="absolute bottom-0 inset-x-0 p-6 sm:p-7">
                    <h3 className="font-display font-bold text-[#F8FAFC] text-xl sm:text-2xl tracking-tight">
                        {pick(item.title)}
                    </h3>
                    <p className="mt-2 text-sm sm:text-[15px] text-[#CBD5E1] leading-relaxed line-clamp-2">
                        {pick(item.outcome)}
                    </p>
                </div>
            </div>

            {/* Details (collapsible) */}
            <details className="group/details bg-white/[0.015] border-t border-white/[0.06]" data-testid={`case-details-${item.id}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none px-6 py-4 select-none text-[13px] text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">
                    <span className="font-semibold tracking-wide uppercase text-[11px] letter-spacing">Mehr Details</span>
                    <ArrowUpRight className="size-4 text-[#10B981] group-open/details:rotate-45 transition-transform duration-300" />
                </summary>
                <div className="px-6 pb-6 grid sm:grid-cols-3 gap-4 text-[13px] leading-relaxed">
                    <Detail label="Ausgangslage" value={pick(item.starting_point)} />
                    <Detail label="Lösung" value={pick(item.solution)} />
                    <Detail label="System" value={pick(item.system)} />
                </div>
            </details>
        </article>
    );
}

function Detail({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#10B981]">{label}</div>
            <div className="mt-1.5 text-[#CBD5E1]">{value}</div>
        </div>
    );
}
